/* PaymentConsolidation — "Tail of the Tail" capability on the Landing Page.
   ProPay Worldwide × Infosys co-branded section. Navy + Cyan palette only.
   Vertical-flow layout (no side-by-side columns at any breakpoint to avoid
   text overlap). CTA routes the visitor into /infoshop?otp=1 which
   auto-opens the OTP dialog. */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileUp, ShieldCheck, Sparkles, Receipt } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentConsolidation() {
  const { t } = useLocale();

  const STEPS = [
    {
      n: "01",
      icon: FileUp,
      title: t("landing.propay.step1.title"),
      body: t("landing.propay.step1.body"),
    },
    {
      n: "02",
      icon: Sparkles,
      title: t("landing.propay.step2.title"),
      body: t("landing.propay.step2.body"),
    },
    {
      n: "03",
      icon: Receipt,
      title: t("landing.propay.step3.title"),
      body: t("landing.propay.step3.body"),
    },
  ];

  return (
    <section
      id="propay"
      data-testid="landing-propay-section"
      className="relative py-24 md:py-28 overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 80% 0%, rgba(0,180,216,0.16), transparent 60%), linear-gradient(180deg, #061A33 0%, #0B2A4A 55%, #061A33 100%)",
      }}
    >
      <div className="absolute inset-0 -z-0 opacity-[0.07] bg-grid-dark" aria-hidden="true" />
      <div
        className="absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#00B4D8]/15 blur-3xl -z-0"
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
        {/* Co-branded eyebrow row */}
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-10 animate-fade-up"
          data-testid="propay-cobrand-strip"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00B4D8]/30 bg-[#00B4D8]/10 text-[11px] uppercase tracking-[0.22em] font-bold text-[#7DD3FC]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00B4D8]" />
            {t("landing.propay.eyebrow")}
          </span>
          <span className="hidden sm:block h-px w-10 bg-white/15" />
          <div className="inline-flex items-center rounded-md bg-white px-3 py-1.5 shadow-sm">
            <img
              src={`${BACKEND_URL}/api/static/site/propay-logo.png`}
              alt="ProPay Worldwide Inc."
              style={{ height: 22, width: "auto" }}
              className="object-contain"
            />
          </div>
          <span className="font-display text-white/90 text-sm font-semibold tracking-tight">
            {t("landing.propay.powered_by")}
          </span>
        </div>

        {/* Big headline (full width, generous max-width) */}
        <h2
          data-testid="propay-headline"
          className="font-display text-white text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05] max-w-4xl animate-fade-up"
        >
          {t("landing.propay.headline_pre")}{" "}
          <span className="italic font-light text-slate-300 whitespace-nowrap">
            {t("landing.propay.headline_em")}
          </span>{" "}
          {t("landing.propay.headline_mid")}{" "}
          <span className="text-[#7DD3FC]">{t("landing.propay.headline_post")}</span>
        </h2>

        {/* Sub-headline */}
        <p
          className="mt-7 text-base sm:text-lg md:text-xl text-slate-300/85 max-w-3xl leading-relaxed font-light animate-fade-up"
          style={{ animationDelay: "120ms" }}
          data-testid="propay-subhead"
        >
          {t("landing.propay.subhead")}
        </p>

        {/* CTA row */}
        <div
          className="mt-10 flex flex-col sm:flex-row gap-3 animate-fade-up"
          style={{ animationDelay: "200ms" }}
        >
          <Link
            to="/infoshop?otp=1&network_id=JAMES.SMITH&company_name=JDECOFFEE&country_name=Netherlands"
            data-testid="propay-cta-primary"
            className="inline-flex items-center justify-center gap-2 rounded-sm px-7 py-4 text-[15px] font-semibold tracking-tight bg-[#00B4D8] text-[#02192F] hover:bg-[#3DD3F0] transition-colors group"
          >
            {t("landing.propay.cta_primary")}
            <ArrowUpRight
              size={18}
              className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
            />
          </Link>
          <a
            href="#propay-flow"
            data-testid="propay-cta-secondary"
            className="inline-flex items-center justify-center rounded-sm px-7 py-4 text-[15px] font-semibold border border-white/15 text-white hover:bg-white/[0.04] hover:border-white/40 transition-colors"
          >
            {t("landing.propay.cta_secondary")}
          </a>
        </div>

        {/* 3-step pipeline — full-width horizontal at md+, vertical at sm */}
        <div
          id="propay-flow"
          className="mt-16 animate-fade-up"
          style={{ animationDelay: "260ms" }}
          data-testid="propay-pipeline"
        >
          <div className="flex items-center gap-2 mb-5">
            <div className="h-2 w-2 rounded-full bg-[#00B4D8] animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
              {t("landing.propay.pipeline_title")}
            </span>
            <span className="ml-auto text-[10px] font-mono text-slate-500">
              / payment-consolidation
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  data-testid={`propay-step-${i}`}
                  className="relative rounded-xl bg-[#02192F]/85 backdrop-blur-xl border border-white/10 p-5 md:p-6 hover:border-[#00B4D8]/30 transition-colors"
                >
                  {/* connector arrows between steps (md+ only) */}
                  {i < STEPS.length - 1 && (
                    <div
                      aria-hidden="true"
                      className="hidden md:block absolute top-1/2 -right-3 translate-x-px -translate-y-1/2 z-10 text-[#00B4D8]/60"
                    >
                      <ArrowUpRight size={18} className="rotate-45" />
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-md bg-[#00B4D8]/10 border border-[#00B4D8]/30 text-[#7DD3FC] flex items-center justify-center shrink-0">
                      <Icon size={18} />
                    </div>
                    <span className="font-mono text-[11px] text-slate-500">{s.n}</span>
                  </div>
                  <div className="font-display text-white font-semibold text-[15px] leading-snug">
                    {s.title}
                  </div>
                  <p className="mt-2 text-sm text-slate-300/80 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trust strip */}
        <div
          className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] text-slate-400 animate-fade-up"
          style={{ animationDelay: "340ms" }}
          data-testid="propay-trust-strip"
        >
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={14} className="text-[#7DD3FC]" />
            {t("landing.propay.trust1")}
          </span>
          <span className="h-3 w-px bg-white/10" />
          <span className="inline-flex items-center gap-1.5">
            <Sparkles size={14} className="text-[#7DD3FC]" />
            {t("landing.propay.trust2")}
          </span>
          <span className="h-3 w-px bg-white/10" />
          <span>{t("landing.propay.trust3")}</span>
        </div>
      </div>
    </section>
  );
}
